import { claudeWatermarkDetector } from '../../lib/labs/claude-watermark-detector';
import {
  analyzeWatermark,
  generateWatermarkSample,
  keyFingerprint,
  type TokenEvidence,
  type WatermarkAnalysis,
  type WatermarkConfig,
  type WatermarkVerdict,
} from '../../lib/labs/text-watermark';

const root = document.querySelector<HTMLElement>('[data-text-watermark]');

if (root) {
  const get = <T extends HTMLElement>(selector: string): T => {
    const element = root.querySelector<T>(selector);
    if (!element) throw new Error(`Missing text-watermark element: ${selector}`);
    return element;
  };

  const keyInput = get<HTMLInputElement>('#watermark-key');
  const keyFingerprintOutput = get<HTMLElement>('#key-fingerprint');
  const contextWidthInput = get<HTMLSelectElement>('#context-width');
  const layersInput = get<HTMLSelectElement>('#watermark-layers');
  const sentenceCountInput = get<HTMLSelectElement>('#sample-length');
  const textInput = get<HTMLTextAreaElement>('#watermark-text');
  const feedback = get<HTMLElement>('#watermark-feedback');
  const sampleOrigin = get<HTMLElement>('#sample-origin');
  const resultPanel = get<HTMLElement>('#watermark-results');
  const resultVerdict = get<HTMLElement>('#result-verdict');
  const resultExplanation = get<HTMLElement>('#result-explanation');
  const resultZ = get<HTMLElement>('#result-z');
  const resultP = get<HTMLElement>('#result-p');
  const resultRate = get<HTMLElement>('#result-rate');
  const resultEvidence = get<HTMLElement>('#result-evidence');
  const resultMeter = get<HTMLElement>('#result-meter');
  const tokenMap = get<HTMLElement>('#token-map');
  const tokenMapNote = get<HTMLElement>('#token-map-note');
  const alternativeTitle = get<HTMLElement>('#token-alternative-title');
  const alternativeContext = get<HTMLElement>('#token-alternative-context');
  const alternativeGroups = get<HTMLElement>('#token-alternative-groups');
  const alternativeNote = get<HTMLElement>('#token-alternative-note');
  const apiStatus = get<HTMLElement>('#claude-api-status');
  let generatedSample = false;

  function readConfig(): WatermarkConfig {
    return {
      key: keyInput.value,
      contextWidth: Number(contextWidthInput.value),
      layers: Number(layersInput.value),
    };
  }

  function setFeedback(message: string, tone: 'muted' | 'error' | 'success' = 'muted') {
    feedback.textContent = message;
    feedback.dataset.tone = tone;
  }

  function updateFingerprint(): void {
    keyFingerprintOutput.textContent = keyFingerprint(keyInput.value);
  }

  function randomKey(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  function randomSeed(): number {
    const values = new Uint32Array(1);
    crypto.getRandomValues(values);
    return values[0];
  }

  function formatPValue(value: number): string {
    if (value < 0.000001) return value.toExponential(2);
    if (value < 0.001) return value.toFixed(6);
    return value.toFixed(4);
  }

  const verdictCopy: Record<WatermarkVerdict, { title: string; explanation: string }> = {
    detected: {
      title: 'Keyed signal detected',
      explanation:
        'The eligible token choices align strongly with this lab key and configuration.',
    },
    weak: {
      title: 'Weak keyed signal',
      explanation:
        "The score is elevated, but it does not cross the lab's strong-evidence threshold.",
    },
    'not-detected': {
      title: 'No keyed signal detected',
      explanation:
        "The eligible token choices are consistent with the lab's null model for this key.",
    },
    insufficient: {
      title: 'Insufficient evidence',
      explanation:
        'There are too few eligible token choices for this lab to make a meaningful comparison.',
    },
  };

  function scoreTone(score: number): 'positive' | 'mixed' | 'negative' {
    if (score >= 2 / 3) return 'positive';
    if (score <= 1 / 3) return 'negative';
    return 'mixed';
  }

  const alternativeToneClasses = {
    positive: {
      chip: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
      dot: 'bg-emerald-400',
    },
    mixed: {
      chip: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
      dot: 'bg-amber-400',
    },
    negative: {
      chip: 'border-rose-500/30 bg-rose-500/10 text-rose-300',
      dot: 'bg-rose-400',
    },
  } as const;

  function renderAlternativeInspector(item: TokenEvidence): void {
    alternativeTitle.textContent = `Alternatives for “${item.token}”`;
    alternativeContext.textContent = `“${item.context.join(' ')}” → ${item.normalized}`;
    alternativeGroups.replaceChildren();

    for (const [groupIndex, group] of item.alternativeGroups.entries()) {
      const row = document.createElement('div');
      row.className = 'grid gap-2 rounded-lg border border-border/60 bg-input-bg/60 p-3';

      if (item.alternativeGroups.length > 1) {
        const label = document.createElement('span');
        label.className = 'text-[0.65rem] uppercase tracking-wider text-muted';
        label.textContent = `Possible set ${groupIndex + 1}`;
        row.append(label);
      }

      const choices = document.createElement('div');
      choices.className = 'flex flex-wrap gap-2';
      for (const candidate of group) {
        const tone = alternativeToneClasses[scoreTone(candidate.score)];
        const choice = document.createElement('span');
        choice.className = `inline-flex items-center gap-2 rounded-lg border px-3 py-2 font-mono text-xs ${tone.chip}`;
        if (candidate.selected) {
          choice.classList.add(
            'ring-1',
            'ring-accent',
            'ring-offset-1',
            'ring-offset-bg'
          );
        }

        const dot = document.createElement('span');
        dot.className = `size-1.5 shrink-0 rounded-full ${tone.dot}`;
        dot.setAttribute('aria-hidden', 'true');

        const word = document.createElement('span');
        word.className = 'font-medium';
        word.textContent = candidate.token;

        const score = document.createElement('span');
        score.className = 'tabular-nums opacity-75';
        score.textContent = `${candidate.positiveBits}/${candidate.trials}`;

        choice.append(dot, word, score);
        if (candidate.selected) {
          const used = document.createElement('span');
          used.className =
            'rounded bg-accent/15 px-1.5 py-0.5 font-sans text-[0.6rem] uppercase tracking-wider text-accent';
          used.textContent = 'used';
          choice.append(used);
        }
        choices.append(choice);
      }
      row.append(choices);
      alternativeGroups.append(row);
    }

    alternativeNote.textContent =
      item.alternativeGroups.length > 1
        ? 'This word occurs in more than one curated choice set, so every possible set is shown. Scores use the current key and exact preceding context.'
        : 'Scores use the current key and exact preceding context. The outlined candidate is the word used in the passage.';
  }

  function renderTokenMap(analysis: WatermarkAnalysis): void {
    tokenMap.replaceChildren();
    alternativeTitle.textContent = 'Word inspector';
    alternativeContext.textContent = 'Select a colored word';
    alternativeGroups.replaceChildren();
    alternativeNote.textContent =
      'Hover, focus, or tap a colored word to compare its alternatives.';
    const limit = 420;
    for (const item of analysis.evidence.slice(0, limit)) {
      const token = document.createElement('span');
      token.className = 'wm-token';
      token.textContent = item.token;
      if (!item.eligible || item.score === null) {
        token.classList.add('wm-token--ineligible');
        token.title = 'Not an eligible choice in this research generator';
      } else if (item.score >= 2 / 3) {
        token.classList.add('wm-token--positive');
        token.title = `${item.positiveBits}/${item.trials} positive keyed observations`;
      } else if (item.score <= 1 / 3) {
        token.classList.add('wm-token--negative');
        token.title = `${item.positiveBits}/${item.trials} positive keyed observations`;
      } else {
        token.classList.add('wm-token--neutral');
        token.title = `${item.positiveBits}/${item.trials} positive keyed observations`;
      }
      if (item.eligible) {
        token.classList.add('wm-token--interactive');
        token.tabIndex = 0;
        token.setAttribute('role', 'button');
        token.setAttribute(
          'aria-label',
          `${item.token}: ${item.positiveBits} of ${item.trials} positive keyed observations. Show alternatives.`
        );
        const showAlternatives = (): void => renderAlternativeInspector(item);
        token.addEventListener('mouseenter', showAlternatives);
        token.addEventListener('focus', showAlternatives);
        token.addEventListener('click', showAlternatives);
      }
      tokenMap.append(token, document.createTextNode(' '));
    }
    tokenMapNote.textContent =
      analysis.evidence.length > limit
        ? `Showing the first ${limit} of ${analysis.evidence.length} tokens.`
        : `Showing ${analysis.evidence.length} tokens.`;
  }

  function renderAnalysis(analysis: WatermarkAnalysis): void {
    const copy = verdictCopy[analysis.verdict];
    resultPanel.dataset.verdict = analysis.verdict;
    resultVerdict.textContent = copy.title;
    resultExplanation.textContent = copy.explanation;
    resultZ.textContent = analysis.zScore.toFixed(2);
    resultP.textContent = formatPValue(analysis.pValue);
    resultRate.textContent = `${(analysis.meanScore * 100).toFixed(1)}%`;
    resultEvidence.textContent = `${analysis.eligibleTokens} tokens / ${analysis.trials} observations`;
    const meterPercent = Math.max(0, Math.min(100, (analysis.zScore / 8) * 100));
    resultMeter.style.width = `${meterPercent}%`;
    renderTokenMap(analysis);
  }

  function analyze(): void {
    try {
      if (!textInput.value.trim())
        throw new Error('Generate or paste text before analyzing it.');
      const analysis = analyzeWatermark(textInput.value, readConfig());
      renderAnalysis(analysis);
      setFeedback(
        generatedSample
          ? 'Analysis updated locally. Edit the sample and run it again to measure the change.'
          : 'Text analyzed locally against the lab key and eligible-vocabulary model.',
        'success'
      );
    } catch (error) {
      setFeedback(
        error instanceof Error ? error.message : 'Unable to analyze the text.',
        'error'
      );
    }
  }

  function generate(watermarked: boolean): void {
    try {
      const config = readConfig();
      const text = generateWatermarkSample({
        ...config,
        watermarked,
        sentenceCount: Number(sentenceCountInput.value),
        seed: randomSeed(),
      });
      textInput.value = text;
      generatedSample = true;
      sampleOrigin.textContent = watermarked ? 'Keyed sample' : 'Control sample';
      sampleOrigin.dataset.origin = watermarked ? 'keyed' : 'control';
      setFeedback(
        watermarked
          ? 'Generated locally with tournament-style keyed selection.'
          : 'Generated locally without keyed selection.',
        'success'
      );
      analyze();
    } catch (error) {
      setFeedback(
        error instanceof Error ? error.message : 'Unable to generate a sample.',
        'error'
      );
    }
  }

  get<HTMLButtonElement>('#generate-watermarked').addEventListener('click', () =>
    generate(true)
  );
  get<HTMLButtonElement>('#generate-control').addEventListener('click', () =>
    generate(false)
  );
  get<HTMLButtonElement>('#analyze-watermark').addEventListener('click', analyze);

  get<HTMLButtonElement>('#random-key').addEventListener('click', () => {
    keyInput.value = randomKey();
    updateFingerprint();
    generatedSample = false;
    sampleOrigin.textContent = 'Key changed';
    sampleOrigin.dataset.origin = 'changed';
    setFeedback("New local key created. Existing text will not carry this key's signal.");
  });

  const toggleKey = get<HTMLButtonElement>('#toggle-key');
  toggleKey.addEventListener('click', () => {
    const reveal = keyInput.type === 'password';
    keyInput.type = reveal ? 'text' : 'password';
    toggleKey.textContent = reveal ? 'Hide' : 'Show';
    toggleKey.setAttribute('aria-pressed', String(reveal));
  });

  keyInput.addEventListener('input', () => {
    updateFingerprint();
    generatedSample = false;
    sampleOrigin.textContent = 'Key changed';
    sampleOrigin.dataset.origin = 'changed';
  });
  textInput.addEventListener('input', () => {
    if (generatedSample) {
      sampleOrigin.textContent = 'Edited sample';
      sampleOrigin.dataset.origin = 'edited';
    }
  });

  get<HTMLButtonElement>('#check-claude-api').addEventListener('click', async () => {
    const result = await claudeWatermarkDetector.detect(textInput.value);
    apiStatus.textContent =
      result.status === 'unavailable'
        ? result.message
        : `${result.label}: ${(result.score * 100).toFixed(1)}%`;
  });

  updateFingerprint();
}
